import { supabase } from './supabase';

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker not supported in this browser');
  }

  try {
    // Register service worker with proper scope
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      updateViaCache: 'none'
    });

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    console.log('Service Worker registered successfully');
    
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw new Error('Failed to register Service Worker');
  }
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    throw new Error('Notifications not supported in this browser');
  }

  try {
    // Always request permission - this will trigger the browser prompt
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    
    if (permission === 'denied') {
      throw new Error('Notification permission denied');
    }

    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    throw error;
  }
}

export async function subscribeToPushNotifications(registration: ServiceWorkerRegistration) {
  try {
    // Check if there's an existing subscription
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      console.log('Using existing push subscription');
      return existingSubscription;
    }

    // Get VAPID public key with fallback strategy
    let vapidPublicKey;
    
    // Try app_config first
    try {
      const { data, error } = await supabase
        .from('app_config')
        .select('value')
        .eq('key', 'VAPID_PUBLIC_KEY')
        .maybeSingle();
        
      if (!error && data?.value) {
        const isValidBase64Url = /^[A-Za-z0-9_-]+$/;
        if (isValidBase64Url.test(data.value)) {
          vapidPublicKey = data.value;
          console.log('Successfully fetched VAPID key from app_config');
        } else {
          console.warn('Invalid VAPID key format in app_config, using default');
        }
      } else {
        console.warn('Error fetching VAPID key from app_config:', error);
      }
    } catch (fetchError) {
      console.warn('Error fetching VAPID key from app_config:', fetchError);
    }
    
    // If not found in app_config, try environment variable
    if (!vapidPublicKey) {
      const envKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      const isValidBase64Url = /^[A-Za-z0-9_-]+$/;
      if (envKey && isValidBase64Url.test(envKey)) {
        vapidPublicKey = envKey;
      }
    }
    
    // If still not found, use default fallback
    if (!vapidPublicKey) {
      console.log('VAPID public key not found, using default fallback');
      vapidPublicKey = 'BNE0YWHICk4XVj_2Xg0FP8k4R8gKENhzEcyVgNsPlE1Wz6zfN_Ke4C_3vqwiHB4SBM3rR9k6rWR3RkeFDtA9UKg';
    }
    
    console.log('Using VAPID public key:', vapidPublicKey);
    const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

    // Subscribe to push notifications with comprehensive error handling
    let subscription;
    try {
      console.log('Subscribing to push notifications...');
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      });
      console.log('Push subscription successful:', subscription);
    } catch (subscribeError) {
      console.error('Initial push subscription failed:', subscribeError);
      
      // If subscription fails with the app_config key, try with environment variable
      if (vapidPublicKey !== import.meta.env.VITE_VAPID_PUBLIC_KEY && import.meta.env.VITE_VAPID_PUBLIC_KEY) {
        console.log('Retrying subscription with environment variable VAPID key');
        try {
          const envVapidKey = urlBase64ToUint8Array(import.meta.env.VITE_VAPID_PUBLIC_KEY);
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: envVapidKey
          });
          console.log('Retry subscription successful');
        } catch (retryError) {
          console.error('Retry subscription also failed:', retryError);
          throw retryError;
        }
      } else {
        throw subscribeError;
      }
    }

    // Save subscription to database with retry logic
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        console.log('Saving push subscription to database for user:', user.id);
        const { data, error } = await supabase
          .from('push_subscriptions')
          .upsert({
            user_id: user.id,
            subscription: JSON.stringify(subscription)
          }, {
            onConflict: 'user_id'
          })
          .select();
          
        if (error) {
          console.error('Failed to save push subscription to database:', error);
          // Try one more time after a short delay
          await new Promise(resolve => setTimeout(resolve, 1000));
          const { error: retryError } = await supabase
            .from('push_subscriptions')
            .upsert({
              user_id: user.id,
              subscription: JSON.stringify(subscription)
            }, {
              onConflict: 'user_id'
            });
            
          if (retryError) {
            console.error('Retry to save push subscription failed:', retryError);
          }
        } else {
          console.log('Push subscription saved to database:', data);
        }
      }
    } catch (dbError) {
      console.error('Failed to save push subscription to database:', dbError);
      // Continue even if database save fails
    }

    return subscription;
  } catch (err) {
    console.error('Failed to subscribe to push notifications:', err);
    throw new Error('Failed to enable push notifications');
  }
}

// Function to play notification sound with fallback mechanism
export async function playNotificationSound(soundUrl: string | null) {
  try {
    if (!soundUrl) return null;
    
    // Primary sound - use the provided URL
    const primarySoundUrl = 'https://qfvdrthqushzkfjniiou.supabase.co/storage/v1/object/public/sounds//mixkit-marimba-ringtone-1359%20-%20Copy.mp3';
    
    // Default sound if the provided URL is empty or invalid
    const defaultSound = '/sounds/notification.mp3';
    const soundToPlay = soundUrl || primarySoundUrl || defaultSound;
    
    console.log('Playing notification sound:', soundToPlay);
    
    // Create a new Audio instance each time to avoid caching issues
    const audio = new Audio(soundToPlay);
    audio.volume = 0.5; // Set a reasonable default volume
    
    // Set up error handling
    audio.onerror = (e) => {
      console.error('Audio error with primary sound:', e);
      
      // Try primary sound if the provided sound fails
      if (soundToPlay !== primarySoundUrl) {
        console.log('Trying primary sound:', primarySoundUrl);
        const primaryAudio = new Audio(primarySoundUrl);
        primaryAudio.volume = 0.5;
        primaryAudio.play().catch(primaryErr => {
          console.error('Primary audio also failed:', primaryErr);
          
          // Try fallback sound if primary sound fails
          if (soundToPlay !== defaultSound && primarySoundUrl !== defaultSound) {
            console.log('Trying fallback sound:', defaultSound);
            const fallbackAudio = new Audio(defaultSound);
            fallbackAudio.volume = 0.5;
            fallbackAudio.play().catch(fallbackErr => {
              console.error('Fallback audio also failed:', fallbackErr);
            });
          }
        });
      }
    };

    // Play the sound
    await audio.play();
    console.log('Sound played successfully');

    return audio;
  } catch (err) {
    console.error('Error playing notification sound:', err);
    
    // Try fallback sound if there's an error
    try {
      const fallbackSound = 'https://qfvdrthqushzkfjniiou.supabase.co/storage/v1/object/public/sounds//mixkit-marimba-ringtone-1359%20-%20Copy.mp3';
      console.log('Trying fallback sound:', fallbackSound);
      const fallbackAudio = new Audio(fallbackSound);
      fallbackAudio.volume = 0.5;
      await fallbackAudio.play();
      return fallbackAudio;
    } catch (fallbackErr) {
      console.error('Fallback sound also failed:', fallbackErr);
      
      // Try another fallback
      try {
        const secondaryFallback = '/sounds/bell.mp3';
        console.log('Trying secondary fallback:', secondaryFallback);
        const secondaryAudio = new Audio(secondaryFallback);
        secondaryAudio.volume = 0.5;
        await secondaryAudio.play();
        return secondaryAudio;
      } catch (secondaryErr) {
        console.error('Secondary fallback also failed:', secondaryErr);
        return null;
      }
    }
  }
}

// Helper function to convert VAPID key with validation
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  try {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  } catch (error) {
    console.error('Error converting VAPID key:', error);
    throw new Error('Invalid VAPID key format');
  }
}